import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { businessApi } from "../services/api";
import "./GuestRoute.css";

/**
 * Guards /admin-login the way ProtectedRoute guards the dashboard, but in reverse: it keeps an
 * already-signed-in partner OUT of the login form and sends them to their dashboard.
 *
 * The app deliberately trusts a saved token without checking its expiry (see AuthContext.js), so
 * `isAuthenticated` (a token AND a stored profile are present) does NOT prove the session is still
 * live. We confirm it against the server with GET /api/business/me, which flows through authClient's
 * interceptors (it refreshes a near-expired token and replays once on 401). A visitor with no saved
 * session skips the probe entirely and drops straight to the form.
 *
 * Two deliberate choices:
 *  - Gate on `isAuthenticated`, not the raw token, so the redirect target matches what
 *    ProtectedRoute will accept. A token without a stored profile would otherwise be sent to the
 *    dashboard, bounced straight back off ProtectedRoute's `!user` guard, and loop.
 *  - Decide exactly once, on arrival (decidedRef). A partner who signs in *on this page* is handed
 *    off to adminLogin's own post-login redirect rather than being re-checked here — otherwise the
 *    in-page login would flip `isAuthenticated`, re-run the probe, and flash the spinner a second
 *    time (or, on a transient /me failure, bounce them back to the form they just submitted).
 */
const GuestRoute = ({ children }) => {
  const { loading, isAuthenticated } = useAuth();
  // Sticky state, not derived from live `isAuthenticated`, so an in-page login can't flip us back
  // into the spinner. 'deciding' -> spinner; 'redirect' -> live session; 'form' -> show the form.
  const [status, setStatus] = useState("deciding");
  const decidedRef = useRef(false);

  useEffect(() => {
    if (loading) return undefined; // wait for AuthContext to hydrate from localStorage
    if (decidedRef.current) return undefined; // decide once, on arrival
    decidedRef.current = true;

    if (!isAuthenticated) {
      setStatus("form"); // no saved session -> straight to the form, no probe, no spinner
      return undefined;
    }

    let active = true;
    businessApi
      .getMyProfile()
      .then(() => {
        if (active) setStatus("redirect"); // the server confirms a live session
      })
      .catch(() => {
        // Expired / invalid / offline -> fall through to the login form. authClient has already
        // cleared a dead session and (if the refresh token is gone too) will hard-redirect here
        // with ?reason=session_expired, which re-mounts with no token and lands on the form.
        if (active) setStatus("form");
      });

    return () => {
      active = false;
    };
  }, [loading, isAuthenticated]);

  if (status === "redirect") {
    return <Navigate to="/admin-dashboard" replace />;
  }

  if (status === "form") {
    return children;
  }

  return (
    <div className="guest-route-spinner-screen">
      {/* The ring is decorative; the text carries the live-region announcement. Putting role on the
          empty ring left Chromium pruning it from the a11y tree (no accessible name). */}
      <div className="guest-route-spinner" aria-hidden="true" />
      <p className="guest-route-spinner-caption" role="status">
        Checking your session&hellip;
      </p>
    </div>
  );
};

export default GuestRoute;
